<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

export default {
  components: { NextButton, Icon },
  props: {
    inbox: { type: Object, required: true },
  },
  setup(props) {
    const statusData = ref(null);
    const loading = ref(true);
    const refreshKey = ref(0);
    const password = ref('');
    const submitting = ref(false);
    const submitError = ref(false);

    const status = computed(() => statusData.value?.status || '');
    const phoneNumber = computed(() => statusData.value?.phone_number || '');
    const hasQr = computed(() => statusData.value?.has_qr === true);
    const errorMessage = computed(() => statusData.value?.error_message || '');

    const isActive = computed(() => status.value === 'active');
    const showQr = computed(
      () => !isActive.value && (hasQr.value || status.value === 'pending_qr')
    );
    const showError = computed(
      () => status.value === 'error' && errorMessage.value
    );
    // The connector reports `pending_password` when the linked account has 2FA
    // (a cloud password) and the QR was approved but sign-in still needs it.
    const needsPassword = computed(() => status.value === 'pending_password');
    const showPasswordError = computed(
      () => submitError.value || errorMessage.value === 'password_rejected'
    );

    const qrPageUrl = computed(() => props.inbox?.qr_url || '');
    const qrImageUrl = computed(() => {
      const url = props.inbox?.qr_image_url || '';
      if (!url) return '';
      return refreshKey.value ? `${url}${url.includes('?') ? '&' : '?'}_ts=${Date.now()}` : url;
    });

    const fetchStatus = async () => {
      if (!props.inbox?.id) return;
      loading.value = true;
      try {
        const { data } = await InboxesAPI.getTelegramUserStatus(props.inbox.id);
        statusData.value = data;
      } catch {
        statusData.value = {
          status: 'error',
          error_message: 'Failed to load status',
        };
      } finally {
        loading.value = false;
      }
    };

    // Poll the connector status while the settings page is open so an admin
    // sees the channel flip to disconnected (and the QR appear) without
    // having to refresh the page manually after a session is killed.
    const POLL_INTERVAL_MS = 30000;
    let pollHandle = null;

    onMounted(() => {
      fetchStatus();
      pollHandle = window.setInterval(fetchStatus, POLL_INTERVAL_MS);
    });

    onBeforeUnmount(() => {
      if (pollHandle !== null) {
        window.clearInterval(pollHandle);
        pollHandle = null;
      }
    });

    const refreshQrUrl = () => {
      refreshKey.value += 1;
    };

    // Relay the 2FA cloud password to the connector. It is never stored on our
    // side; the connector consumes it once to finish sign-in. The connector
    // validates it asynchronously, so we re-poll a couple of times to surface
    // the result (active, or `password_rejected`) without the 30s poll delay.
    const submitPassword = async () => {
      if (!password.value || submitting.value) return;
      submitting.value = true;
      submitError.value = false;
      try {
        await InboxesAPI.submitTelegramUserPassword(props.inbox.id, password.value);
        password.value = '';
        await fetchStatus();
        window.setTimeout(fetchStatus, 2500);
        window.setTimeout(fetchStatus, 6000);
      } catch {
        submitError.value = true;
      } finally {
        submitting.value = false;
      }
    };

    // --- History sync (backfill of past private dialogs) ---
    const sync = ref({
      state: 'idle',
      percent: 0,
      total: 0,
      done: 0,
      messages: 0,
      current: '',
      error: '',
    });
    const syncStarting = ref(false);
    let syncPollHandle = null;

    const syncRunning = computed(
      () => syncStarting.value || sync.value.state === 'running'
    );
    const syncIsDone = computed(() => sync.value.state === 'done');
    const syncHasError = computed(() => sync.value.state === 'error');

    const stopSyncPoll = () => {
      if (syncPollHandle !== null) {
        window.clearInterval(syncPollHandle);
        syncPollHandle = null;
      }
    };

    const applySync = data => {
      sync.value = {
        state: data?.state || 'idle',
        percent: data?.percent || 0,
        total: data?.total || 0,
        done: data?.done || 0,
        messages: data?.messages || 0,
        current: data?.current || '',
        error: data?.error || '',
      };
    };

    const startSyncPoll = () => {
      stopSyncPoll();
      syncPollHandle = window.setInterval(fetchSyncStatus, 2000);
    };

    async function fetchSyncStatus() {
      if (!props.inbox?.id) return;
      try {
        const { data } = await InboxesAPI.getTelegramUserSyncStatus(
          props.inbox.id
        );
        applySync(data);
        if (sync.value.state === 'running') {
          if (syncPollHandle === null) startSyncPoll();
        } else {
          stopSyncPoll();
        }
      } catch {
        // ignore transient status errors; keep last known state
      }
    }

    const startSync = async () => {
      if (!props.inbox?.id || syncRunning.value) return;
      syncStarting.value = true;
      try {
        const { data } = await InboxesAPI.syncTelegramUserHistory(
          props.inbox.id
        );
        applySync({ ...data, state: data?.state || 'running' });
        startSyncPoll();
      } catch {
        sync.value = { ...sync.value, state: 'error', error: 'failed to start' };
      } finally {
        syncStarting.value = false;
      }
    };

    // Resume showing progress if the page is reopened mid-sync.
    onMounted(fetchSyncStatus);
    onBeforeUnmount(stopSyncPoll);

    return {
      statusData,
      loading,
      status,
      phoneNumber,
      hasQr,
      errorMessage,
      isActive,
      showQr,
      showError,
      qrImageUrl,
      qrPageUrl,
      refreshKey,
      fetchStatus,
      refreshQrUrl,
      needsPassword,
      showPasswordError,
      password,
      submitting,
      submitPassword,
      sync,
      syncRunning,
      syncIsDone,
      syncHasError,
      startSync,
    };
  },
};
</script>

<template>
  <div class="max-connection-card mx-8 mt-5 overflow-hidden rounded-xl border border-n-weak bg-n-surface-2 shadow-sm">
    <div class="border-b border-n-weak bg-n-surface-1 px-4 py-3">
      <h3 class="flex items-center gap-2 text-sm font-semibold text-n-slate-12">
        <Icon icon="i-ri-telegram-line" class="text-base text-n-slate-11" />
        {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.TITLE') }}
      </h3>
    </div>
    <div class="p-4">
      <div v-if="loading" class="flex items-center gap-2 text-sm text-n-slate-11">
        <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-n-slate-8 border-t-n-slate-11" />
        {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.LOADING') }}
      </div>
      <template v-else>
        <div
          v-if="isActive"
          class="flex flex-col gap-1 rounded-lg bg-n-green-2/50 px-4 py-3 ring-1 ring-n-green-6/30"
        >
          <div class="flex items-center gap-2">
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-n-green-9/20">
              <Icon icon="i-lucide-check" class="h-4 w-4 text-n-green-11" />
            </span>
            <span class="text-sm font-medium text-n-green-11">
              {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.CONNECTED') }}
            </span>
          </div>
          <p v-if="phoneNumber" class="pl-10 text-sm text-n-slate-11">
            {{ phoneNumber }}
          </p>
        </div>
        <div
          v-else-if="showError"
          class="flex gap-3 rounded-lg border border-n-ruby-6 bg-n-ruby-2/50 px-4 py-3"
        >
          <Icon icon="i-lucide-alert-circle" class="mt-0.5 h-5 w-5 shrink-0 text-n-ruby-10" />
          <div class="min-w-0 text-sm text-n-ruby-11">
            <span class="font-medium">{{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.ERROR_LABEL') }}:</span>
            {{ errorMessage }}
          </div>
        </div>
        <div
          v-if="needsPassword"
          class="flex flex-col gap-3 rounded-lg border border-n-amber-6 bg-n-amber-2/40 px-4 py-3"
        >
          <div class="flex gap-3">
            <Icon icon="i-lucide-shield-alert" class="mt-0.5 h-5 w-5 shrink-0 text-n-amber-10" />
            <div class="min-w-0 text-sm text-n-slate-12">
              <p class="font-medium">
                {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.PASSWORD_REQUIRED') }}
              </p>
              <p class="mt-1 text-n-slate-11">
                {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.PASSWORD_HINT') }}
              </p>
            </div>
          </div>
          <form class="flex flex-wrap items-center gap-2" @submit.prevent="submitPassword">
            <input
              v-model="password"
              type="password"
              autocomplete="off"
              :placeholder="$t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.PASSWORD_PLACEHOLDER')"
              class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
            <NextButton
              sm
              :is-loading="submitting"
              :disabled="!password || submitting"
              :label="
                submitting
                  ? $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.PASSWORD_SUBMITTING')
                  : $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.PASSWORD_SUBMIT')
              "
              @click="submitPassword"
            />
          </form>
          <p v-if="showPasswordError" class="text-sm text-n-ruby-11">
            {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.PASSWORD_ERROR') }}
          </p>
        </div>
        <div v-if="showQr || showError" class="mt-4 space-y-3 border-t border-n-weak pt-4">
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.QR_HINT') }}
          </p>
          <a
            v-if="qrPageUrl"
            :href="qrPageUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-4 py-2.5 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-slate-3"
          >
            <Icon icon="i-ri-qr-code-2-line" class="h-4 w-4 text-n-slate-11" />
            {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.OPEN_QR') }}
          </a>
          <div class="flex flex-wrap gap-2">
            <NextButton
              sm
              slate
              :label="$t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.REFRESH')"
              @click="fetchStatus"
            />
            <NextButton
              v-if="showQr && qrImageUrl"
              sm
              slate
              :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.REFRESH_QR_BUTTON')"
              @click="refreshQrUrl"
            />
          </div>
        </div>
        <div v-if="isActive" class="mt-4 space-y-3 border-t border-n-weak pt-4">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.SYNC_TITLE') }}
            </p>
            <p class="text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.SYNC_HINT') }}
            </p>
          </div>
          <NextButton
            sm
            solid
            blue
            :is-loading="syncRunning"
            :disabled="syncRunning"
            :label="
              syncRunning
                ? $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.SYNC_RUNNING')
                : $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.SYNC_BUTTON')
            "
            @click="startSync"
          />
          <div v-if="syncRunning || syncIsDone" class="space-y-1.5">
            <div class="h-2 w-full overflow-hidden rounded-full bg-n-slate-4">
              <div
                class="h-full rounded-full bg-n-blue-9 transition-all duration-500"
                :style="{ width: `${sync.percent}%` }"
              />
            </div>
            <p class="text-xs text-n-slate-11">
              {{ sync.percent }}% ·
              {{
                $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.SYNC_PROGRESS', {
                  done: sync.done,
                  total: sync.total,
                  messages: sync.messages,
                })
              }}<template v-if="syncRunning && sync.current">
                · {{ sync.current }}</template>
            </p>
          </div>
          <p v-if="syncIsDone" class="text-sm font-medium text-n-green-11">
            {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.SYNC_DONE') }}
          </p>
          <p v-if="syncHasError" class="text-sm text-n-ruby-11">
            {{ $t('INBOX_MGMT.TELEGRAM_USER_CONNECTION_STATUS.SYNC_ERROR_LABEL') }}: {{ sync.error }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
